<template>
  <div class="w-paginator">
    <!-- Page buttons -->
    <div class="w-paginator__pages">

      <!-- Previous -->
      <b-button
        variant="link"
        class="w-paginator__arrow"
        :disabled="isFirstPage"
        @click="previousPage"
      >
        <font-awesome-icon icon="arrow-left" />
      </b-button>

      <!-- Page numbers -->
      <b-button
        v-for="item in visiblePageItems"
        :key="item.key"
        class="w-paginator__page"
        :class="{ 'w-paginator__page--active': item.page === currentPage }"
        :disabled="item.isEllipsis"
        @click="selectPage(item.page)"
      >
        {{ item.label }}
      </b-button>

      <!-- Next -->
      <b-button
        variant="link"
        class="w-paginator__arrow"
        :disabled="isLastPage"
        @click="nextPage"
      >
        <font-awesome-icon icon="arrow-right" />
      </b-button>

    </div>

    <!-- Per page selector -->
    <div
      v-if="showPerPageSelector"
      class="w-paginator__per-page"
    >
      <span>{{ translate("show") }}</span>

      <b-form-select
        v-model="selectedPerPage"
        :options="perPageOptions"
        class="w-paginator__select"
      />

      <span>{{ translate("per page") }}</span>
    </div>

    <!-- Page info -->
    <div class="w-paginator__info">
      {{ translate("page") }} {{ currentPage }} {{ translate("of") }} {{ totalPages }}
    </div>
  </div>
</template>

<script>
import {wordlist,translate_word} from '../assets/js/translate.js';

export default {
  name: "Paginator",

  props: {
    /*
      v-model value.
      The parent controls the current page.
    */
    value: {
      type: Number,
      default: 1
    },

    /*
      Total number of records.
      Example: 42 archived wines.
    */
    totalRows: {
      type: Number,
      required: true
    },

    /*
      Number of records per page.
      Use .sync from the parent if you want this to be updateable.
    */
    perPage: {
      type: Number,
      default: 6
    },

    /*
      Dropdown values for "items per page".
    */
    perPageOptions: {
      type: Array,
      default() {
        return [
          { value: 6, text: "6" },
          { value: 12, text: "12" },
          { value: 24, text: "24" },
          { value: 48, text: "48" }
        ];
      }
    },

    /*
      Number of page buttons around the current page.
      The first and last page are always shown.
    */
    maxVisiblePages: {
      type: Number,
      default: 5
    },

    showPerPageSelector: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    currentLanguage() {
      return this.$store.state.language
    },

    currentPage() {
      return this.value;
    },

    totalPages() {
      const pages = Math.ceil(this.totalRows / this.perPage);
      return Math.max(1, pages);
    },

    isFirstPage() {
      return this.currentPage <= 1;
    },

    isLastPage() {
      return this.currentPage >= this.totalPages;
    },

    selectedPerPage: {
      get() {
        return this.perPage;
      },

      set(value) {
        const newPerPage = Number(value);

        this.$emit("update:perPage", newPerPage);
        this.$emit("per-page-change", newPerPage);

        /*
          When the number of items per page changes,
          go back to the first page.
        */
        this.$emit("input", 1);
        this.$emit("page-change", 1);
      }
    },

    visiblePageItems() {
      const total = this.totalPages;
      const current = this.currentPage;
      const max = this.maxVisiblePages;

      /*
        Small number of pages:
        show everything.
      */
      if (total <= max + 2) {
        return this.createPageRange(1, total);
      }

      const items = [];

      /*
        Always show first page.
      */
      items.push(this.createPageItem(1));

      let start = current - 2;
      let end = current + 2;

      /*
        Near the beginning.
      */
      if (current <= 3) {
        start = 2;
        end = max;
      }

      /*
        Near the end.
      */
      if (current >= total - 2) {
        start = total - max + 1;
        end = total - 1;
      }

      start = Math.max(2, start);
      end = Math.min(total - 1, end);

      /*
        Ellipsis between first page and middle pages.
      */
      if (start > 2) {
        items.push(this.createEllipsisItem("start"));
      }

      /*
        Middle pages.
      */
      items.push(...this.createPageRange(start, end));

      /*
        Ellipsis between middle pages and last page.
      */
      if (end < total - 1) {
        items.push(this.createEllipsisItem("end"));
      }

      /*
        Always show last page.
      */
      items.push(this.createPageItem(total));

      return items;
    }
  },

  watch: {
    /*
      If the amount of data changes and the current page
      no longer exists, move to the last valid page.
    */
    totalPages(newTotalPages) {
      if (this.currentPage > newTotalPages) {
        this.selectPage(newTotalPages);
      }
    }
  },

  methods: {
    previousPage() {
      this.selectPage(this.currentPage - 1);
    },

    nextPage() {
      this.selectPage(this.currentPage + 1);
    },

    selectPage(page) {
      if (!page) {
        return;
      }

      const safePage = Math.min(
        Math.max(page, 1),
        this.totalPages
      );

      if (safePage === this.currentPage) {
        return;
      }

      this.$emit("input", safePage);
      this.$emit("page-change", safePage);
    },

    createPageRange(start, end) {
      const pages = [];

      for (let page = start; page <= end; page++) {
        pages.push(this.createPageItem(page));
      }

      return pages;
    },

    createPageItem(page) {
      return {
        key: `page-${page}`,
        page,
        label: page,
        isEllipsis: false
      };
    },

    createEllipsisItem(position) {
      return {
        key: `ellipsis-${position}`,
        page: null,
        label: "…",
        isEllipsis: true
      };
    },

    translate(string) {
      return translate_word(string, this.currentLanguage);
    },
  }
};
</script>

<style scoped>
.w-paginator {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
}

.w-paginator__pages {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.w-paginator__page {
  min-width: 38px;
  height: 38px;
  padding: 0 0.75rem;

  border: 1px solid #d7dce2;
  border-radius: 10px;

  background-color: #ffffff;
  color: #102a43;

  font-weight: 600;
  box-shadow: none;
}

.w-paginator__page:hover {
  background-color: #f7f7f7;
}

.w-paginator__page--active,
.w-paginator__page--active:hover,
.w-paginator__page--active:focus,
.w-paginator__page--active:active {
  background-color: #6d7a52 !important;
  border-color: #6d7a52 !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

.w-paginator__page:focus,
.w-paginator__page:active {
  box-shadow: none !important;
}

.w-paginator__page:disabled {
  background-color: #ffffff;
  color: #7b8794;
  opacity: 1;
}

.w-paginator__arrow {
  color: #102a43;
  text-decoration: none;
  box-shadow: none;
}

.w-paginator__arrow:hover {
  color: #6d7a52;
  text-decoration: none;
}

.w-paginator__arrow:disabled {
  color: #b0b7c3;
}

.w-paginator__per-page {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.w-paginator__select {
  width: 80px;
}

.w-paginator__info {
  font-size: 0.95rem;
  color: #102a43;
}

/* Mobile */
@media (max-width: 576px) {
  .w-paginator__pages {
    gap: 0.35rem;
  }

  .w-paginator__page {
    min-width: 34px;
    height: 34px;
    padding: 0 0.55rem;
  }

  .w-paginator__per-page {
    font-size: 0.9rem;
  }
}
</style>